import { useQuery } from "@tanstack/react-query"
import { z } from "zod"
import { auth } from "@/lib/firebase"

// Define schemas for type validation
const bucketItemSchema = z.object({
	bucket: z.string(),
	count: z.number(),
})

const documentsPerCaseSchema = z.object({
	average: z.number(),
	median: z.number(),
	min_count: z.number(),
	max_count: z.number(),
	total_documents: z.number(),
	total_cases: z.number(),
	days_period: z.number(),
	distribution: z.array(bucketItemSchema),
})

export type BucketItem = z.infer<typeof bucketItemSchema>
export type DocumentsPerCaseStats = z.infer<typeof documentsPerCaseSchema>

// Fetch function
const fetchDocumentsPerCaseStats = async (
	days: number
): Promise<DocumentsPerCaseStats> => {
	const user = auth.currentUser
	if (!user) {
		throw new Error("User not found.")
	}

	const token = await user.getIdToken()

	const response = await fetch(
		`${
			import.meta.env.VITE_API_URL
		}/api/stats/documents-per-case/?days=${days}`,
		{
			headers: {
				Authorization: `Bearer ${token}`,
			},
		}
	)

	if (!response.ok) {
		throw new Error(
			`Failed to fetch documents per case stats: ${response.status} ${response.statusText}`
		)
	}

	const data = await response.json()
	return documentsPerCaseSchema.parse(data)
}

// React Query hook
export const useDocumentsPerCaseStats = (days: number = 7) => {
	return useQuery({
		queryKey: ["documentsPerCaseStats", days],
		queryFn: () => fetchDocumentsPerCaseStats(days),
	})
}
